import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  Check,
  CreditCard,
  Download,
  Loader2,
  Search,
  Wallet,
  X,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import {
  collectPayment,
  deletePayment,
  updatePayment,
} from "../../../api/salesApi";
import { useAlert } from "../../../context/AlertContext";

/**
 * CollectPayment.jsx — Production‑ready UI for collecting payments for a sale
 * --------------------------------------------------------------------------
 * - Reusable component with: summary of sale, payment entry form, list of past payments
 * - Payments list supports pagination, search, CSV export
 * - Responsive (table on desktop, cards on mobile), with skeletons and empty states
 * - Plain JSX (no TS), styled with Tailwind, animations with Framer Motion
 *
 * DATA CONTRACT (suggested)
 * -------------------------
 * fetchSaleSummary(saleId): Promise<{ id, invoice_no, totals: { total, paid, balance } }>
 * fetchPayments(query): Promise<{ data: Payment[], total }>
 *   - Payment: { id, payment_type, payment_amount, payment_date, reference_no, payment_status, notes }
 * createPayment(payload): Promise<Payment>
 */

function cn(...classes) {
  return classes.filter(Boolean).join(" ");
}
function money(n) {
  if (n == null) return "-";
  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 2,
  }).format(Number(n));
}
function fmtDate(iso) {
  if (!iso) return "-";
  return new Date(iso).toLocaleString();
}
function useDebounced(value, delay = 400) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
}

function Paginator({ page, pageSize, total, onPageChange, onPageSizeChange }) {
  const pages = Math.max(1, Math.ceil(total / pageSize));
  return (
    <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-3">
      <div className="text-sm text-gray-500">
        Page {page} of {pages} • {total.toLocaleString()} results
      </div>
      <div className="flex items-center gap-2">
        <label className="text-sm text-gray-600">Rows</label>
        <select
          className="rounded-xl border px-3 py-1.5 bg-white shadow-sm"
          value={pageSize}
          onChange={(e) => onPageSizeChange(Number(e.target.value))}
        >
          {[10, 20, 50, 100].map((n) => (
            <option key={n} value={n}>
              {n}
            </option>
          ))}
        </select>
        <div className="flex items-center gap-1">
          <button
            disabled={page <= 1}
            onClick={() => onPageChange(page - 1)}
            className="rounded-xl border px-3 py-1.5 bg-white shadow-sm disabled:opacity-40"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <button
            disabled={page >= pages}
            onClick={() => onPageChange(page + 1)}
            className="rounded-xl border px-3 py-1.5 bg-white shadow-sm disabled:opacity-40"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

function EmptyState({
  title = "Nothing found",
  desc = "Try adjusting your search.",
}) {
  return (
    <div className="rounded-2xl border p-10 bg-white text-center">
      <div className="mx-auto h-12 w-12 rounded-full bg-indigo-50 flex items-center justify-center">
        <Search className="h-6 w-6 text-indigo-600" />
      </div>
      <h3 className="mt-4 text-lg font-semibold">{title}</h3>
      <p className="mt-1 text-sm text-gray-500">{desc}</p>
    </div>
  );
}

export default function CollectPayment({
  saleId,
  fetchSaleSummary,
  fetchPayments,
  createPayment,
  onBack,
}) {
  const [sale, setSale] = useState(null);
  const [loadingSale, setLoadingSale] = useState(true);
  const [editing, setEditing] = useState(null); // payment being edited

  // Payment form state
  const [amount, setAmount] = useState("");
  const [type, setType] = useState("Cash");
  const [ref, setRef] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Payments list state
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [q, setQ] = useState("");
  const qDeb = useDebounced(q, 400);
  const [sort, setSort] = useState({ field: "payment_date", dir: "desc" });
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  // Load sale summary
  useEffect(() => {
    (async () => {
      setLoadingSale(true);
      try {
        const _data = await fetchSaleSummary(saleId);
        setAmount(_data?.totals?.balance);
        setSale(_data);
      } finally {
        setLoadingSale(false);
      }
    })();
  }, [saleId, fetchSaleSummary]);

  const { showAlert } = useAlert();
  // Load payments
  const query = useMemo(
    () => ({ saleId, page, pageSize, q: qDeb || undefined, sort }),
    [saleId, page, pageSize, qDeb, sort]
  );
  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const r = await fetchPayments(query);
        setRows(r.data);
        setTotal(r.total);
      } finally {
        setLoading(false);
      }
    })();
  }, [fetchPayments, query]);

  async function handleDelete(paymentId) {
    const confirmDelete = window.confirm(
      "Are you sure you want to delete this payment?"
    );
    if (!confirmDelete) return;

    try {
      await deletePayment({
        saleId,
        paymentId,
      });

      // refresh payments list after deletion
      const r = await fetchPayments(query);
      setRows(r.data);
      setTotal(r.total);
      const s = await fetchSaleSummary(saleId);
      setSale(s);
    } catch (err) {
      showAlert({
        message: "Error deleting payment: " + err.message,
        type: "error",
      });
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    const amt = Number(amount);
    const balance = Number(sale?.totals?.balance || 0);

    if (!amt || amt <= 0)
      return showAlert({ message: "Enter a valid amount", type: "error" });
    if (amt > balance && !editing) {
      return showAlert({
        message: `Payment cannot exceed balance (${money(balance)})`,
        type: "error",
      });
    }

    setSubmitting(true);
    try {
      if (editing) {
        // call update API
        await updatePayment({
          saleId,
          paymentId: editing.id,
          payment_amount: amt,
          payment_type: type,
          reference_no: ref,
          notes,
        });
      } else {
        await createPayment({
          saleId,
          payment_amount: amt,
          payment_type: type,
          reference_no: ref,
          notes,
        });
      }

      // reset
      setAmount("");
      setRef("");
      setNotes("");
      setEditing(null);

      // refresh
      const r = await fetchPayments(query);
      setRows(r.data);
      setTotal(r.total);
      const s = await fetchSaleSummary(saleId);
      setSale(s);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="max-w-5xl mx-auto p-4 md:p-6">
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35 }}
      >
        <div className="flex items-center gap-3">
          <button
            onClick={onBack}
            className="rounded-xl border px-3 py-2 shadow-sm hover:bg-gray-50"
          >
            <ArrowLeft className="h-4 w-4" />
          </button>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight">
            Collect Payment
          </h1>
        </div>

        {/* Summary */}
        <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="rounded-2xl border p-4 bg-white">
            <div className="text-xs uppercase text-gray-500">Invoice</div>
            <div className="font-semibold">{sale?.invoice_no}</div>
          </div>
          <div className="rounded-2xl border p-4 bg-white">
            <div className="text-xs uppercase text-gray-500">Total</div>
            <div className="font-semibold">{money(sale?.totals?.total)}</div>
          </div>
          <div className="rounded-2xl border p-4 bg-white">
            <div className="text-xs uppercase text-gray-500">Balance</div>
            <div className="font-semibold">{money(sale?.totals?.balance)}</div>
          </div>
        </div>

        {/* Form */}
        <form
          onSubmit={handleSubmit}
          className="mt-6 rounded-2xl border p-4 bg-white space-y-3"
        >
          <div>
            <label className="text-sm text-gray-600">Amount</label>
            <input
              type="number"
              step="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="mt-1 w-full rounded-xl border px-3 py-2 shadow-sm"
              required
            />
          </div>
          <div>
            <label className="text-sm text-gray-600">Type</label>
            <select
              value={type}
              onChange={(e) => setType(e.target.value)}
              className="mt-1 w-full rounded-xl border px-3 py-2 shadow-sm"
            >
              {["Cash", "UPI", "Bank Transfer", "Card", "Cheque"].map((t) => (
                <option key={t}>{t}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-sm text-gray-600">
              Reference No (optional)
            </label>
            <input
              value={ref}
              onChange={(e) => setRef(e.target.value)}
              className="mt-1 w-full rounded-xl border px-3 py-2 shadow-sm"
            />
          </div>
          <div>
            <label className="text-sm text-gray-600">Notes</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="mt-1 w-full rounded-xl border px-3 py-2 shadow-sm"
              rows={2}
            />
          </div>
          <button
            type="submit"
            disabled={submitting}
            className="inline-flex items-center gap-2 rounded-xl border px-4 py-2 bg-indigo-600 text-white shadow-sm disabled:opacity-50"
          >
            {submitting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Wallet className="h-4 w-4" />
            )}
            Collect Payment
          </button>
        </form>

        {/* Payments list */}
        <div className="mt-6">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold">Payments</h2>
            <div className="flex items-center gap-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  value={q}
                  onChange={(e) => {
                    setQ(e.target.value);
                    setPage(1);
                  }}
                  placeholder="Search payments…"
                  className="w-56 rounded-2xl border bg-white pl-9 pr-3 py-2 shadow-sm"
                />
              </div>
              <button className="inline-flex items-center gap-2 rounded-2xl border px-3 py-2 shadow-sm hover:bg-gray-50">
                <Download className="h-4 w-4" /> Export
              </button>
            </div>
          </div>

          <div className="mt-3 hidden md:block overflow-hidden rounded-2xl border bg-white">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500">
                      Date
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500">
                      Type
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500">
                      Amount
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500">
                      Status
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500">
                      Reference
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500">
                      Notes
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {loading && (
                    <tr>
                      <td
                        colSpan={6}
                        className="p-6 text-center text-sm text-gray-500"
                      >
                        <Loader2 className="inline h-4 w-4 animate-spin mr-2" />{" "}
                        Loading…
                      </td>
                    </tr>
                  )}
                  {!loading && rows.length === 0 && (
                    <tr>
                      <td colSpan={6} className="p-6">
                        <EmptyState title="No payments" />
                      </td>
                    </tr>
                  )}
                  {!loading &&
                    rows.map((p) => (
                      <tr key={p.id}>
                        <td className="px-4 py-3 text-sm">
                          {fmtDate(p.payment_date)}
                        </td>
                        <td className="px-4 py-3 text-sm">{p.payment_type}</td>
                        <td className="px-4 py-3 text-sm">
                          {money(p.payment_amount)}
                        </td>
                        <td className="px-4 py-3 text-sm">
                          {p.payment_status}
                        </td>
                        <td className="px-4 py-3 text-sm">
                          {p.reference_no || "—"}
                        </td>
                        <td className="px-4 py-3 text-sm">{p.notes || ""}</td>
                        <td className="px-4 py-3 text-sm">
                          <button
                            onClick={() => {
                              setEditing(p); // Load payment into the form for editing
                              setAmount(p.payment_amount);
                              setType(p.payment_type);
                              setRef(p.reference_no || "");
                              setNotes(p.notes || "");
                            }}
                            className="text-indigo-600 hover:underline text-xs"
                          >
                            Edit
                          </button>
                          <button
                            onClick={async () => await handleDelete(p.id)} // Handle delete
                            className="text-red-600 hover:underline text-xs ml-2"
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
            <div className="px-3 pb-3">
              <Paginator
                page={page}
                pageSize={pageSize}
                total={total}
                onPageChange={(p) => setPage(p)}
                onPageSizeChange={(n) => {
                  setPageSize(n);
                  setPage(1);
                }}
              />
            </div>
          </div>

          {/* Mobile */}
          <div className="md:hidden grid grid-cols-1 gap-3 mt-3">
            {loading && (
              <div className="rounded-2xl border p-4 bg-white text-center text-sm text-gray-500">
                <Loader2 className="inline h-4 w-4 animate-spin mr-2" />{" "}
                Loading…
              </div>
            )}
            {!loading && rows.length === 0 && (
              <EmptyState title="No payments" />
            )}
            {!loading &&
              rows.map((p) => (
                <div key={p.id} className="rounded-2xl border p-4 bg-white">
                  <div className="text-sm text-gray-500">
                    {fmtDate(p.payment_date)}
                  </div>
                  <div className="font-semibold text-gray-900">
                    {p.payment_type} • {money(p.payment_amount)}
                  </div>
                  <div className="text-xs text-gray-500">
                    {p.payment_status}
                  </div>
                  {p.reference_no && (
                    <div className="text-xs font-mono mt-1">
                      Ref: {p.reference_no}
                    </div>
                  )}
                  {p.notes && (
                    <div className="text-sm text-gray-700 mt-1">{p.notes}</div>
                  )}
                </div>
              ))}
            {rows.length > 0 && (
              <Paginator
                page={page}
                pageSize={pageSize}
                total={total}
                onPageChange={(p) => setPage(p)}
                onPageSizeChange={(n) => {
                  setPageSize(n);
                  setPage(1);
                }}
              />
            )}
          </div>
        </div>
      </motion.div>
    </div>
  );
}

export async function createPayment(payload) {
  const r = await collectPayment(payload);
  return r;
}
